  if (!customElements.get('yeschef-class-calendar')) {
    customElements.define('yeschef-class-calendar', class extends HTMLElement {
      async connectedCallback() {
        if (this.initialized) {
          if (this.onResize) this.media.addEventListener('change', this.onResize);
          return;
        }
        if (!this.querySelector('[data-list]')) {
          if (!this.childObserver) {
            this.childObserver = new MutationObserver(() => {
              if (this.querySelector('[data-list]') && this.querySelector('[data-pagination]')) {
                this.childObserver.disconnect();
                this.connectedCallback();
              }
            });
            this.childObserver.observe(this, {childList:true, subtree:true});
          }
          return;
        }
        this.initialized = true;
        this.list = this.querySelector('[data-list]');
        this.grid = this.querySelector('[data-grid]');
        this.timezone = this.dataset.timezone || 'America/Vancouver';
        this.locale = this.dataset.locale || 'en';
        this.media = window.matchMedia('(max-width:749px)');
        const requestedView = new URL(location.href).searchParams.get('class_view');
        this.view = ['list', 'calendar'].includes(requestedView) ? requestedView : (this.dataset.defaultView || 'calendar');
        try {
          await this.loadPages();
        } catch (error) {
          this.querySelector('[data-load-error]').hidden = false;
          return;
        }
        this.events = [...this.querySelectorAll('[data-event]')].map(card => ({card, date:new Date(card.dataset.start)})).filter(event => !Number.isNaN(event.date.getTime()));
        const now = new Date();
        this.events = this.events.filter(event => this.dataset.showPast === 'true' || event.date >= now).sort((a,b) => a.date-b.date);
        this.list.replaceChildren(...this.events.map(event => event.card));
        this.events.forEach(event => {
          event.key = this.dateKey(event.date);
          const format = options => window.YCClassTime.format(event.date, this.locale, options, this.timezone);
          const badge = event.card.querySelector('[data-date-badge]');
          badge.setAttribute('aria-label', format({dateStyle:'full', timeStyle:'short'}));
          event.card.querySelector('[data-month]').textContent = format({month:'long', year:'numeric'});
          event.card.querySelector('[data-day]').textContent = format({day:'numeric'});
          event.card.querySelector('[data-weekday]').textContent = format({weekday:'long'});
          event.card.querySelector('[data-time]').textContent = format({hour:'numeric', minute:'2-digit'});
          const title = event.card.querySelector('h3');
          title.textContent = title.textContent.replace(/\s*[-–—]\s*(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[\s\S]*$/i, '').trim();
        });
        const today = this.dateKey(now);
        this.todayKey = today;
        const initial = this.events.find(event => event.date >= now)?.key || today;
        this.month = initial.slice(0,7);
        const months = [today.slice(0,7), ...this.events.map(event => event.key.slice(0,7))].sort();
        this.firstMonth = months[0];
        this.lastMonth = months.at(-1);
        this.querySelectorAll('[data-view]').forEach(button => button.addEventListener('click', () => {
          this.view = button.dataset.view;
          const url = new URL(location.href);
          url.searchParams.set('class_view', this.view);
          window.history.replaceState(window.history.state, '', url);
          this.render();
        }));
        this.querySelector('[data-previous]').addEventListener('click', () => this.changeMonth(-1));
        this.querySelector('[data-next]').addEventListener('click', () => this.changeMonth(1));
        this.onResize = () => this.render();
        this.media.addEventListener('change', this.onResize);
        this.querySelector('.yc-calendar__toolbar').hidden = false;
        this.render();
      }
      disconnectedCallback() { this.media?.removeEventListener('change', this.onResize); }
      dateKey(date) {
        const parts = window.YCClassTime.parts(date, 'en-US', {year:'numeric',month:'2-digit',day:'2-digit'}, this.timezone);
        return ['year','month','day'].map(type => parts.find(part => part.type===type).value).join('-');
      }
      async loadPages() {
        let next = this.querySelector('[data-next-page]')?.getAttribute('href');
        const seen = new Set();
        while (next) {
          const url = new URL(next, location.href);
          const current = new URL(location.href);
          url.searchParams.set('view', current.searchParams.get('view') || 'classes-calendar');
          if (current.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'));
          if (url.origin !== location.origin || seen.has(url.href)) throw new Error('Invalid pagination');
          seen.add(url.href);
          const response = await fetch(url, {credentials:'same-origin'});
          if (!response.ok) throw new Error('Unable to load classes');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const calendar = doc.querySelector('yeschef-class-calendar');
          if (!calendar) throw new Error('Calendar not found');
          const ids = new Set([...this.list.querySelectorAll('[data-event]')].map(card => card.dataset.productId));
          calendar.querySelectorAll('[data-event]').forEach(card => { if (!ids.has(card.dataset.productId)) this.list.append(card); });
          next = calendar.querySelector('[data-next-page]')?.getAttribute('href');
        }
        this.querySelector('[data-pagination]').hidden = true;
      }
      changeMonth(offset) {
        const [year,month] = this.month.split('-').map(Number);
        const date = new Date(Date.UTC(year,month-1+offset,1));
        const next = date.toISOString().slice(0,7);
        if (next < this.firstMonth || next > this.lastMonth) return;
        this.month=next;
        this.render();
      }
      render() {
        const calendarView = this.view === 'calendar' && !this.media.matches && this.events.length > 0;
        this.grid.hidden = !calendarView;
        this.list.hidden = calendarView;
        this.querySelector('.yc-calendar__months').hidden = !calendarView;
        this.querySelector('[data-empty]').hidden = this.events.length > 0;
        this.querySelectorAll('[data-view]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.view === (calendarView ? 'calendar' : 'list'))));
        if (!calendarView) return;
        const [year, month] = this.month.split('-').map(Number);
        const first = new Date(Date.UTC(year,month-1,1));
        this.querySelector('[data-month]').textContent = new Intl.DateTimeFormat(this.locale,{month:'long',year:'numeric',timeZone:'UTC'}).format(first);
        this.querySelector('[data-previous]').disabled = this.month <= this.firstMonth;
        this.querySelector('[data-next]').disabled = this.month >= this.lastMonth;
        const weekdays = document.createElement('div');
        weekdays.className = 'yc-calendar__weekdays';
        for (let day=0;day<7;day++) {
          const label = document.createElement('div');
          label.className='yc-calendar__weekday';
          label.textContent=new Intl.DateTimeFormat(this.locale,{weekday:'short',timeZone:'UTC'}).format(new Date(Date.UTC(2026,0,4+day)));
          weekdays.append(label);
        }
        const days = document.createElement('div');
        days.className='yc-calendar__days';
        const count=Math.ceil((first.getUTCDay()+new Date(Date.UTC(year,month,0)).getUTCDate())/7)*7;
        for (let index=0;index<count;index++) {
          const date = new Date(Date.UTC(year,month-1,1-first.getUTCDay()+index));
          const key = date.toISOString().slice(0,10);
          const cell = document.createElement('div');
          cell.className='yc-calendar__day';
          if (date.getUTCMonth() !== month-1) cell.classList.add('yc-calendar__day--outside');
          if (key===this.todayKey) cell.classList.add('yc-calendar__day--today');
          const number = document.createElement('time');
          number.className='yc-calendar__day-number';
          number.dateTime=key;
          number.textContent=String(date.getUTCDate());
          cell.append(number);
          this.events.filter(event => event.key===key).forEach(event => {
            cell.classList.add('yc-calendar__day--event');
            const card=event.card.cloneNode(true);
            card.removeAttribute('data-event');
            card.querySelector('[data-date-badge]').remove();
            card.querySelector('.yc-calendar__copy').prepend(card.querySelector('[data-time]'));
            card.querySelector('[data-time]').textContent=window.YCClassTime.format(event.date, this.locale, {hour:'numeric',minute:'2-digit'}, this.timezone);
            cell.append(card);
          });
          days.append(cell);
        }
        this.grid.replaceChildren(weekdays,days);
      }
    });
  }
